<template>
	<div class="container">
		<div class="page page-settings">
			<!-- Header -->
			<div class="page-header">
				<div class="row">
					<div class="col-md-8">
						<h1 class="page-title">{{ $t('Homepage') }}</h1>
						<div>Atur tampilan homepage</div>
					</div>
					<div class="col-md-4">
						<div class="page-header-right text-right">
							<button type="button" class="btn-outline outline-primary o-md" :class="{ 'is-loading': isSaving }" data-dismiss="modal" @click="saveSection()"><i class="bi-hdd"></i> Save Sections</button>
							<button class="btn-outline outline-primary o-md active ml-2" data-toggle="modal" data-target="#sectionModal"><i class="bi-plus-square"></i> Add Section</button>
						</div>
					</div>
				</div>
			</div>

			<div class="page-content">
				<draggable
					v-model="sections"
					group="people"
					@start="drag=true"
					@end="drag=false"
					handle=".drag-item"
				>
					<div class="card card-body card-widget-container mb-4" v-for="(section, index) in sections" :key="section.id">
						<div class="d-flex">
							<button class="btn btn-sm btn-plain drag-item widget-button-action" v-tooltip="'Drag to order'">
								<i class="bi-grip-vertical"></i>
							</button>
							<span class="widget-title align-self-center mr-auto">
								{{ section.name }}
							</span>
							<button class="btn btn-sm btn-plain" v-tooltip="'Remove this section'" @click="removeSection(index)">
								<i class="bi-x-lg"></i>
							</button>
							<button class="btn btn-sm btn-plain widget-button-action" v-tooltip="'Click to expand'" data-toggle="collapse" :data-target="`#section${section.id}`" aria-expanded="false" :aria-controls="`section${section.id}`">
								<i class="bi-chevron-down"></i>
							</button>
						</div>
						<div class="collapse" :id="`section${section.id}`">
							<div class="card card-body mt-4">
								<div class="font-weight-bold">{{ section.description }}</div>
								<component v-bind:is="section.type" :config="section.config" :slug="section.slug" :meta="section.meta" />
							</div>
						</div>
					</div>
				</draggable>

				<div class="empty-section" v-if="sections.length < 1">
					<div class="card card-body card-widget-container mb-4">
						<div class="d-flex">
							<button class="btn btn-sm btn-plain drag-item widget-button-action">
								<i class="bi-x-circle"></i>
							</button>
							<span class="widget-title align-self-center mr-auto">
								Belum ada data section
							</span>
						</div>
					</div>
				</div>

				<!-- debug : for dev only -->
				<!-- <hr>
				<div>
					{{ sections }}
				</div> -->
			</div>
		</div>

		<div class="modal" tabindex="-1" id="sectionModal">
			<div class="modal-dialog modal-md">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Tambah Section</h5>
						<button type="button" class="close" data-dismiss="modal" aria-label="Close">
							<i class="bi bi-x-lg"></i>
						</button>
					</div>
					<div class="modal-body">
						<div class="form-group">
							<label>Section</label>
							<multiselect
								placeholder="Pilih Section"
								:label="'text'"
								:track-by="'id'"
								v-model="selectedSection"
								:options="sectionsOptions"
								:show-labels="false"
								:allow-empty="false"
							>
							<template slot="option" slot-scope="props">
								<div class="option__desc">
									<span class="option__title">{{ props.option.text }}</span><br>
									<small class="option__small text-muted">{{ props.option.desc }}</small>
								</div>
							</template>
							</multiselect>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn-outline outline-gray o-sm" data-dismiss="modal">Batal</button>
						<button type="button" class="btn-outline outline-primary o-sm" data-dismiss="modal" @click="addNewSection">Tambah</button>
					</div>
				</div>
			</div>
		</div>

		<notifications group="homepages" position="bottom right" />
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import homepageApi from '@/api/homepage';
import { homepageSections, sectionConfigTemplate } from '@/lib/statics';
import { duplicateVar, getAxiosErrorMessage } from '@/lib/helper';
import SellingPoint from '@/components/homepages/admin/SellingPoint.vue';
import SingleBanner from '@/components/homepages/admin/SingleBanner.vue';
import SellingPointWithText from '@/components/homepages/admin/SellingPointWithText.vue';
import Featured from '@/components/homepages/admin/Featured.vue';
import TopCategory from '@/components/homepages/admin/TopCategory.vue';
import ProductCategory from '@/components/homepages/admin/ProductCategory.vue';
import TwoColumnBanner from '@/components/homepages/admin/TwoColumnBanner.vue';
import FourColumnBanner from '@/components/homepages/admin/FourColumnBanner.vue';

export default {
	name: 'AdminHomepage',
	components: {
		draggable,
		SellingPoint,
		SingleBanner,
		SellingPointWithText,
		Featured,
		TopCategory,
		ProductCategory,
		TwoColumnBanner,
		FourColumnBanner,
	},
	data() {
		return {
			isShowModalProductApprovalTime: false,
			isFetching: false,
			isSaving: false,
			params: [],
			currentApprovalTime: '',
			productApprovalTime: '',
			selectedSection: null,
			sections: [],
			configTemplate: sectionConfigTemplate,
		};
	},
	methods: {
		...mapActions({}),
		modifyConfig(section, configIdx = 0) { // default widget config position index is at 0, except recommended product section
			const config = section.config[configIdx];
			if (config === undefined) {
				if (configIdx === 12) { // recommended product widget config index position
					section.config.push(duplicateVar(this.configTemplate));
				} else {
					const configArray = [];
					configArray.push(duplicateVar(this.configTemplate));
					this.$set(section, 'config', configArray); // widget config position at section.config[0]
				}
			} else {
				const sectionConfigData = {
					...duplicateVar(this.configTemplate), // create config from template. edit template to add config property
					...config, // keep previous widget config if exists. this will update value of assigned template
				};
				section.config[configIdx] = sectionConfigData; // widget config position is either at section.config[0] or at section.config[6]
				return section;
			}
			return section;
		},
		checkSlug(section) {
			let result = null;
			if (section.slug === 'best-margin' || section.slug === 'top-product' || section.slug === 'new-product') {
				result = this.modifyConfig(section);
			} else if (section.slug === 'recomended-product') {
				result = this.modifyConfig(section, 12); // recommended product widget config position index is at 6, after 6 product objects
			} else {
				result = section;
			}
			return result;
		},
		transformConfig(sections) { // check and update widget config if there's modification on statics/sectionConfigTemplate
			if (sections) {
				sections = sections.map(section => this.checkSlug(section));
			}
			return sections;
		},
		fetchSections() {
			this.isFetching = true;

			const params = {};

			const callback = (response) => {
				const responseData = response.data;
				this.sections = this.transformConfig(responseData.data.sections);
				this.isFetching = false;
			};

			const errorCallback = () => {
				this.isFetching = false;
			};

			homepageApi.getSection(params, callback, errorCallback);
		},
		addNewSection() {
			const tempSections = this.sections;
			tempSections.push({
				...this.selectedSection,
				id: this.$uuid.v4(),
			});
			this.sections = tempSections;
			this.selectedSection = null;
		},
		saveSection() {
			this.isSaving = true;
			const params = {
				sections: this.sections,
			};
			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'homepages',
					title: 'Save Sections',
					text: message,
					type: 'success',
				});
				this.isSaving = false;
				this.fetchSections();
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'homepages',
					title: 'Save Sections',
					text: message,
					type: 'error',
				});
				this.isSaving = false;
			};

			homepageApi.createOrUpdateSection(params, callback, errorCallback);
		},
		removeSection(index) {
			const tempSections = this.sections;
			tempSections.splice(index, 1);
			this.sections = tempSections;
		},
	},
	computed: {
		...mapGetters({}),
		sectionsOptions() {
			const sections = homepageSections.map((section) => {
				section.id = section.type;
				section.text = section.name;
				section.desc = section.description;
				return section;
			});
			return sections;
		},
	},
	created() {
		this.fetchSections();
	},
};
</script>
<style lang="scss" scoped>
	.card-widget-container {
		border: 2px solid #4D4D4D;
		border-radius: 10px;

		.widget-title {
			font-weight: 700;
			font-size: 22px;
			line-height: 120%;
		}

		.widget-button-action {
			font-weight: 700;
			font-size: 16px;
		}
	}

	.modal {
		.modal-dialog{
			overflow-y: initial !important
		}
		// .modal-body{
		// 	height: 200px;
		// 	overflow-y: auto;
		// }
		.modal-header{
			background-color: transparent;
		}
		.modal-content{
			border-radius: 16px;
		}
		.modal-footer {
			border-top: none;
			background-color: transparent;
		}
	}
</style>
